import type { IndustrialCaseSummary } from '@/domain/industrialCase/industrialCaseSummary';
import Link from '@/routing/AppLink';
import React from 'react';

import { IconArrowRightRound } from '@/components/apps/ui/icon';

import { buildIndustrialCasePath } from '@/components/apps/pages/solution/industrial/industrialCaseRoutes';

import theme from '@/styles/theme';

type Props = {
  caseSummary: IndustrialCaseSummary;
  readLabel: string;
  metricLabel: string;
  onSelect: () => void;
};

/**
 * ### HomeIndustrialCaseCard
 *
 * Renders one published customer case in the dark home-page case surface.
 * The card keeps its content tied to the board-backed summary and links to the
 * same localized detail route as the public case list.
 *
 * @param props Published case summary and interaction labels
 * @returns The linked customer-case card
 */
export function HomeIndustrialCaseCard({
  caseSummary,
  readLabel,
  metricLabel,
  onSelect,
}: Props) {
  const classification = [
    caseSummary.industry,
    caseSummary.process,
    caseSummary.inspectionTarget,
  ].filter((value) => value.trim() !== '');

  return (
    <Link
      className="group flex h-full min-h-[360px] min-w-0 flex-col justify-between gap-8 rounded-[20px] border border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.05)] p-6 text-left shadow-[0_8px_40px_rgba(0,0,0,0.2)] backdrop-blur-[30px] transition-[border-color,background-color,transform] duration-200 ease-out hover:-translate-y-1 hover:border-[rgba(255,255,255,0.26)] hover:bg-[rgba(255,255,255,0.08)] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-apps-corePrimary01 motion-reduce:transform-none motion-reduce:transition-none lg:p-8"
      href={buildIndustrialCasePath(caseSummary.slug)}
      prefetch={false}
      onClick={onSelect}
      aria-label={`${caseSummary.title} ${readLabel}`}
    >
      <div className="min-w-0">
        {classification.length > 0 && (
          <p className="text-[13px] font-bold leading-[150%] tracking-[-0.26px] text-apps-corePrimary03">
            {classification.join(' · ')}
          </p>
        )}
        <h3 className="mt-4 text-[24px] font-bold leading-[130%] tracking-[-0.48px] text-apps-coreGrayWhite text-balance">
          {caseSummary.title}
        </h3>
        <p className="mt-4 line-clamp-5 text-[15px] font-medium leading-[165%] tracking-[-0.3px] text-apps-brandGray800 [word-break:keep-all]">
          {caseSummary.summary}
        </p>
      </div>

      <div className="min-w-0">
        {caseSummary.primaryMetric.value && (
          <div className="border-t border-[rgba(255,255,255,0.14)] pt-5">
            <span className="text-[13px] font-semibold leading-[140%] text-apps-brandGray800">
              {caseSummary.primaryMetric.label || metricLabel}
            </span>
            <strong className="mt-2 block text-[32px] font-extrabold leading-[115%] tracking-[-0.04em] text-apps-coreGrayWhite [font-variant-numeric:tabular-nums]">
              {caseSummary.primaryMetric.value}
            </strong>
          </div>
        )}
        {/* 카드 전체가 링크라 버튼 요소를 겹치지 않고, 버튼처럼 보이는 표지로 둔다 */}
        <span className="mt-6 inline-flex min-h-[44px] items-center gap-2 rounded-full bg-apps-corePrimary01 px-5 text-sm font-bold leading-[140%] text-apps-coreGrayWhite transition-colors duration-200 group-hover:bg-apps-corePrimary02 motion-reduce:transition-none">
          {readLabel}
          <IconArrowRightRound
            className="transition-transform duration-200 ease-out group-hover:translate-x-1 motion-reduce:transform-none motion-reduce:transition-none"
            size={16}
            color={theme`colors.apps.coreGrayWhite`}
            aria-hidden="true"
          />
        </span>
      </div>
    </Link>
  );
}
